/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { useMsal } from "@azure/msal-react";
import { Modal, PrimaryButton, Stack, Text } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  REFRESH_THRESHOLD,
  TOKEN_CHECK_INTERVAL,
  getATK,
  graphRequest,
  kqlRequest,
  loginRequest,
  oneClickRequest,
  powerBiRequest
} from "./authConfig";
import MainComp from "./main";
import { updateUserLog } from "./Services/user-loging.services";
import { updateAuthResult, updateGraphToken, updateOneClickCxReportToken, updatePbiToken } from "./slices/Auth.slice";
import { useAppDispatch } from "./store/hooks";

const Login = () => {
  const { instance, accounts } = useMsal();
  const activeAccountRef = useRef(null);
  const interval = useRef(null);
  const timeoutRef = useRef(null);
  const dispatch = useAppDispatch();
  const [isShow, setIsShow] = useState(false);
  const [isUserLogCalled, setIsUserLogCalled] = useState(false);
  const [isTimeoutModalVisible, setIsTimeoutModalVisible] = useState(false);

  const getPromises = (msal) => [
    getATK(msal, graphRequest.scopes, activeAccountRef.current),
    getATK(msal, powerBiRequest.scopes, activeAccountRef.current),
    getATK(msal, oneClickRequest.scopes, activeAccountRef.current),
    getATK(msal, kqlRequest.scopes, activeAccountRef.current)
  ];
  const onEnableShow = useCallback(() => {
    setIsShow(true);
  }, []);
  const onHandleUserLogCalled = useCallback((status) => {
    setIsUserLogCalled(status);
  }, []);

  const acquireTokenWithRefreshToken = (isLoginRedirect = false) => {
    const activeAccount = activeAccountRef.current;
    if (!activeAccount) {
      return;
    }

    instance
      .acquireTokenSilent({
        ...loginRequest,
        account: activeAccount
      })
      .then((value) => {
        const { username, name } = activeAccount;
        sessionStorage.setItem("token", value.accessToken);
        sessionStorage.setItem("loginName", name);
        sessionStorage.setItem("loginUserName", username);
        sessionStorage.setItem("expiresOn", value.expiresOn);
        dispatch(updateAuthResult(value));
        return Promise.allSettled(getPromises(instance));
      })
      .then((values) => {
        for (let idx = 0; idx < values.length; idx++) {
          const { status, value } = values[idx];
          if (status === "fulfilled") {
            const token = value.accessToken;
            switch (idx) {
              case 0:
                dispatch(updateGraphToken(token));

                if (isLoginRedirect && !isUserLogCalled) {
                  updateUserLog(token, sessionStorage.getItem("token"));
                  onHandleUserLogCalled(true);
                }

                break;
              case 1:
                dispatch(updatePbiToken(token));
                break;
              case 2:
                dispatch(updateOneClickCxReportToken(token));
                break;
              case 3:
                sessionStorage.setItem("kqlToken", token);
                break;
              default:
                break;
            }
            onEnableShow();
          }
        }
      })
      .catch((error) => {
        // Silent token acquisition failed
        if (
          error.errorCode === "monitor_window_timeout" ||
          error.errorCode === "interaction_required" ||
          error.errorCode === "consent_required" ||
          error.errorCode === "login_required" ||
          error.errorCode === "token_renewal_error"
        ) {
          setIsTimeoutModalVisible(true);
        }
        return;
      });
  };

  const checkTokenExpiry = () => {
    const token = sessionStorage.getItem("token");
    const expiresOn = sessionStorage.getItem("expiresOn");

    if (token) {
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const timeUntilExpiry = Math.floor(new Date(expiresOn) / 1000) - currentTime;
      if (timeUntilExpiry <= REFRESH_THRESHOLD) {
        // Token is about to expire or has expired, refresh it
        acquireTokenWithRefreshToken();
      }
    }
  };

  // Check if the session storage has cleared the token or its expiry data, and if so, redirect to login
  const onStorageChange = () => {
    const token = sessionStorage.getItem("token");
    const expiresOn = sessionStorage.getItem("expiresOn");

    if (!token || !expiresOn) {
      instance.loginRedirect(loginRequest);
    }
  };

  // User has switched back to the tab
  const onFocus = () => {
    // Check and generate the token when the token is expired already
    // Do not wait for the 1 minute and call checkTokenExpiry immediate
    checkTokenExpiry();
    // Setup checkTokenExpiry for every 1 minute until the tab is focused
    interval.current = setInterval(checkTokenExpiry, TOKEN_CHECK_INTERVAL);
  };
  // User has switched away from the tab (AKA tab is hidden)
  const onBlur = () => {
    clearInterval(interval.current);
    interval.current = null;
  };

  // Handle button click to clear timeout and redirect immediately
  const handleTimeoutModalButtonClick = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    instance.loginRedirect(loginRequest);
  };

  // Handle automatic redirect after timeout when modal is visible
  useEffect(() => {
    if (isTimeoutModalVisible) {
      // Set timeout to redirect after 5 seconds
      timeoutRef.current = setTimeout(() => {
        instance.loginRedirect(loginRequest);
      }, 5000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [isTimeoutModalVisible, instance]);

  useEffect(() => {
    instance.handleRedirectPromise().then((response) => {
      window.location.hash = "";
      if (!response && !accounts.length) {
        instance.loginRedirect(loginRequest);
      } else {
        activeAccountRef.current = response ? response.account : accounts.at(0);
        acquireTokenWithRefreshToken(!!response);
      }
    });

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorageChange);
    // Calls onFocus when the window first loads
    onFocus();
    // Specify how to clean up after this effect:
    return () => {
      clearInterval(interval.current);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  return (
    <>
      <Modal isOpen={isTimeoutModalVisible} isBlocking>
        <Stack
          tokens={{ childrenGap: 20 }}
          horizontalAlign="center"
          verticalAlign="center"
          styles={{ root: { padding: 20 } }}
        >
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
            Session Timeout
          </Text>
          <Text variant="medium">
            Your session has timed out. You will be redirected to the login page in a few seconds.
          </Text>
          <PrimaryButton text="Go to Login Now" onClick={handleTimeoutModalButtonClick} />
        </Stack>
      </Modal>
      {isShow && <MainComp />}
    </>
  );
};

export default Login;
